"use client";

import * as React from "react";
import { PhoneCall, ShieldCheck, Clock, SunMedium, Wrench, ArrowRight, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const faqData = [{
  id: "item-1",
  category: "PERMITS",
  categoryIcon: Clock,
  question: "Do home additions need permits?",
  answer: "Yes. Additions require a building permit, plans and usually structural engineering. We coordinate the plans and the city inspections."
}, {
  id: "item-2",
  category: "PLANNING",
  categoryIcon: ShieldCheck,
  question: "Where do I start with an addition?",
  answer: "Start with how you want to use the new space. We visit your home, review your lot and setbacks, and give you a written scope and estimate."
}, {
  id: "item-3",
  category: "LIVING",
  categoryIcon: SunMedium,
  question: "Can we stay home during an addition?",
  answer: "Usually, yes. Much of the work happens outside the existing home until the addition is tied in, and we plan that phase with you."
}, {
  id: "item-4",
  category: "SERVICE AREA",
  categoryIcon: Wrench,
  question: "Where do you work?",
  answer: "San Bernardino, Riverside, Orange and Los Angeles counties."
}];
export default function Kitchenremodelingfaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.32,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section data-section-id="189009" id="kitchenremodelingfaq" data-nav="light" className="bg-background text-foreground border-b border-border py-16 md:py-24">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqData.map(f => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: String(f.answer)
          }
        }))
      })
    }} />
      <div className="mx-auto max-w-5xl px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <motion.div initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 16
      }} whileInView={shouldReduceMotion ? undefined : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-12 text-center">
          <div className="inline-flex items-center gap-2 font-mono text-xs font-semibold tracking-wider text-primary uppercase">
            <HelpCircle className="size-3.5" />
            <span>Common questions</span>
          </div>
          <h2 className="font-serif mt-3 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Home addition FAQs
          </h2>
          <p className="font-default text-muted-foreground mx-auto mt-4 max-w-2xl text-base sm:text-lg">
            Answers to common questions about our home addition process and service areas.
          </p>
        </motion.div>

        {/* Accordion List */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="mx-auto max-w-4xl space-y-4">
          <Accordion type="single" collapsible className="space-y-4">
            {faqData.map((item, i) => {
            const IconComp = item.categoryIcon;
            return <motion.div key={item.id} data-index={i} variants={itemVariants} className="bg-card text-card-foreground rounded-lg border border-border shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                  <AccordionItem value={item.id} className="border-none px-6 py-1">
                    <AccordionTrigger className="font-serif text-foreground hover:no-underline py-4 text-left text-base sm:text-lg font-semibold">
                      <div className="flex flex-col items-start gap-2 pr-4 sm:flex-row sm:items-center sm:gap-3">
                        <Badge variant="secondary" className="font-mono text-[11px] tracking-wide text-secondary-foreground bg-secondary uppercase border-none">
                          <IconComp className="size-3 mr-1" />
                          {item.category}
                        </Badge>
                        <span className="leading-snug">{item.question}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="font-default text-muted-foreground pt-1 pb-5 text-sm leading-relaxed sm:text-base">
                      <div className="border-t border-border/60 pt-3">
                        {item.answer}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>;
          })}
          </Accordion>
        </motion.div>

        {/* Emergency Help & Inspection Action Bar */}
        <motion.div initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 16
      }} whileInView={shouldReduceMotion ? undefined : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.35,
        delay: 0.2,
        ease: [0.16, 1, 0.3, 1]
      }} className="bg-card text-card-foreground border-border mt-12 rounded-xl border p-6 shadow-sm md:p-8">
          <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
            <div className="max-w-xl space-y-2">
              <div className="inline-flex items-center gap-2">
                <span className="relative flex size-2.5">
                  <span className="bg-primary absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"></span>
                  <span className="bg-primary relative inline-flex size-2.5 rounded-full"></span>
                </span>
                <span className="font-mono text-xs font-semibold tracking-wider text-primary uppercase">
                  Ready to start?
                </span>
              </div>
              <h3 className="font-serif text-xl font-bold tracking-tight text-foreground sm:text-2xl">
                Have a project in mind?
              </h3>
              <p className="font-default text-muted-foreground text-sm leading-normal sm:text-base">
                Contact us today to discuss your addition or request an estimate.
              </p>
            </div>

            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
              <Button asChild variant="default" size="lg" className="font-medium">
                <a href="tel:+19099063355" className="flex items-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>Call us</span>
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="font-medium">
                <Link to="/#request-estimate" className="flex items-center gap-2">
                  <span>Request estimate</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}